// src/store/slices/nodesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  NodeDto, 
  NodeDetailDto, 
  NodeHierarchyResponse, 
  NodesState, 
  NodeSearchParams,
  NodeHierarchyParams,

  // =============================================================================================
  // ==== Dodane w Krok 2 >>>>
  ImObjectDto,        
  NodeFormsResponse 
  // <<<< Dodane w Krok 2 ====
  // =============================================================================================
  

} from '../../types/nodes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5218';

// Async thunks
export const fetchNodeHierarchy = createAsyncThunk(
  'nodes/fetchHierarchy',
  async (params: NodeHierarchyParams = {}) => {
    const token = localStorage.getItem('token');
    const queryParams = new URLSearchParams();
    
    if (params.parentNodeId) queryParams.append('parentNodeId', params.parentNodeId.toString());
    if (params.includeChildren !== undefined) queryParams.append('includeChildren', params.includeChildren.toString());
    if (params.maxDepth) queryParams.append('maxDepth', params.maxDepth.toString());

    const response = await fetch(`${API_BASE_URL}/api/nodes/hierarchy?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch node hierarchy');
    }

    return await response.json() as NodeHierarchyResponse;
  }
);

export const fetchNodeDetail = createAsyncThunk(
  'nodes/fetchDetail',
  async (nodeId: number) => {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/nodes/${nodeId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch node detail');
    }

    return await response.json() as NodeDetailDto;
  }
);

export const searchNodes = createAsyncThunk(
  'nodes/search',
  async (params: NodeSearchParams) => {
    const token = localStorage.getItem('token');
    const queryParams = new URLSearchParams();
    
    if (params.barCodeId) queryParams.append('barCodeId', params.barCodeId);
    if (params.nfcId) queryParams.append('nfcId', params.nfcId);
    if (params.name) queryParams.append('name', params.name);

    const response = await fetch(`${API_BASE_URL}/api/nodes/search?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to search nodes');
    }

    return await response.json() as NodeDto[];
  }
);

// Initial state
const initialState: NodesState = {
  hierarchy: [],
  selectedNode: null,
  expandedNodes: [],
  searchResults: [],
  loading: false,
  error: null,
  metadata: {},

  // =============================================================================================
  // ==== Dodane w Krok 2 >>>>
  selectedNodeForms: [],
  formsLoading: false,
  formsError: null,
  // <<<< Dodane w Krok 2 ====
  // =============================================================================================


};

// Slice
const nodesSlice = createSlice({
  name: 'nodes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    
    setSelectedNode: (state, action: PayloadAction<NodeDetailDto | null>) => {
      state.selectedNode = action.payload;
    },
    
    toggleNodeExpansion: (state, action: PayloadAction<number>) => {
      const nodeId = action.payload;
      const newExpanded = new Set(state.expandedNodes);
      
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId);
      } else {
        newExpanded.add(nodeId);
      }
      
      state.expandedNodes = Array.from(newExpanded);
    },
    
    expandNode: (state, action: PayloadAction<number>) => {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.add(action.payload);
      state.expandedNodes = Array.from(newExpanded);
    },
    
    collapseNode: (state, action: PayloadAction<number>) => {
      const newExpanded = new Set(state.expandedNodes);
      newExpanded.delete(action.payload);
      state.expandedNodes = Array.from(newExpanded);
    },
    
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    
    reset: () => initialState,
  },
  
  extraReducers: (builder) => {
    // Fetch hierarchy
    builder
      .addCase(fetchNodeHierarchy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNodeHierarchy.fulfilled, (state, action) => {
        state.loading = false;
        state.hierarchy = action.payload.nodes;
        state.metadata = action.payload.metadata;
        state.error = null;
      })
      .addCase(fetchNodeHierarchy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch node hierarchy';
      });

    // Fetch node detail
    builder
      .addCase(fetchNodeDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNodeDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedNode = action.payload;
        state.error = null;
      })
      .addCase(fetchNodeDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch node detail';
      });

    // Search nodes
    builder
      .addCase(searchNodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchNodes.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
        state.error = null;
      })
      .addCase(searchNodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search nodes';
      });

    // =============================================================================================
    // ==== Dodane w Krok 2 >>>>
    builder
        .addCase(fetchNodeForms.pending, (state) => {
          state.formsLoading = true;
          state.formsError = null;
        })
        .addCase(fetchNodeForms.fulfilled, (state, action) => {
          state.formsLoading = false;
          state.selectedNodeForms = action.payload.availableForms;
          state.formsError = null;
        })
        .addCase(fetchNodeForms.rejected, (state, action) => {
          state.formsLoading = false;
          state.formsError = action.payload || action.error.message || 'Failed to fetch node forms';
        });    
    // <<<< Dodane w Krok 2 ====
    // =============================================================================================    


  },
});

export const {
  clearError,
  setSelectedNode,
  toggleNodeExpansion,
  expandNode,
  collapseNode,
  clearSearchResults,
  reset,
} = nodesSlice.actions;

export default nodesSlice.reducer;

// =============================================================================================
// ==== Dodane w Krok 2 >>>>

// W src/store/slices/nodesSlice.ts - dodaj na końcu pliku:

// Nowy async thunk dla formularzy
export const fetchNodeForms = createAsyncThunk<
  NodeFormsResponse,
  number,
  { rejectValue: string }
>(
  'nodes/fetchNodeForms',
  async (nodeId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/nodes/${nodeId}/forms`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);
 
// <<<< Dodane w Krok 2 ====
// =============================================================================================
